import { useEffect, useMemo, useState } from 'react';
import {
  Announcements,
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FlexColumn } from '@elementary/common-ui/components';
import { RuleAccordionProps } from './RuleAccordion';
import SortableRuleAccordion from './SortableRuleAccordion';

type Props = {
  data: RuleAccordionProps[];
  onReorder: (orderedIds: string[]) => void;
  disabled?: boolean;
};

const SortableRuleList = ({
  data: dataFromProps,
  onReorder,
  disabled,
}: Props) => {
  // Keep the order locally so a drop reorders the list in the same render
  // in which dnd-kit resets the drag transforms.
  const [data, setData] = useState(dataFromProps);

  useEffect(() => {
    setData(dataFromProps);
  }, [dataFromProps]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const ids = useMemo(() => data.map((item) => item.id), [data]);

  const announcements: Announcements = useMemo(() => {
    const describe = (id: UniqueIdentifier) =>
      data.find((item) => item.id === id)?.name ?? '';
    const position = (id: UniqueIdentifier) =>
      `position ${ids.indexOf(String(id)) + 1} of ${ids.length}`;

    return {
      onDragStart: ({ active }) =>
        `Picked up rule ${describe(active.id)} at ${position(active.id)}.`,
      onDragOver: ({ active, over }) =>
        over
          ? `Rule ${describe(active.id)} moved to ${position(over.id)}.`
          : `Rule ${describe(active.id)} is not over a position.`,
      onDragEnd: ({ active, over }) =>
        over
          ? `Rule ${describe(active.id)} dropped at ${position(over.id)}.`
          : `Rule ${describe(active.id)} dropped.`,
      onDragCancel: ({ active }) =>
        `Reordering cancelled. Rule ${describe(active.id)} kept its position.`,
    };
  }, [data, ids]);

  const moveRule = (fromIndex: number, toIndex: number) => {
    const reordered = arrayMove(data, fromIndex, toIndex);
    setData(reordered);
    onReorder(reordered.map((item) => item.id));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    moveRule(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{ announcements }}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <FlexColumn sx={{ gap: 2 }}>
          {data.map((item, i) => (
            <SortableRuleAccordion
              key={item.id}
              {...item}
              index={item.index === undefined ? undefined : i + 1}
              disabled={disabled}
              onMoveUp={
                !disabled && i > 0 ? () => moveRule(i, i - 1) : undefined
              }
              onMoveDown={
                !disabled && i < data.length - 1
                  ? () => moveRule(i, i + 1)
                  : undefined
              }
            />
          ))}
        </FlexColumn>
      </SortableContext>
    </DndContext>
  );
};

export default SortableRuleList;
